/**
 * Touch gesture detection hook for swipe interactions
 * Supports horizontal and vertical swipes with configurable thresholds
 */

import * as React from 'react';

export interface SwipeGestureOptions {
  /** Minimum distance in pixels to trigger a swipe (default: 50) */
  threshold?: number;
  /** Maximum time in ms for a swipe gesture (default: 500) */
  maxTime?: number;
  /** Enable horizontal swipe detection (default: true) */
  horizontal?: boolean;
  /** Enable vertical swipe detection (default: false) */
  vertical?: boolean;
  /** Prevent default touch events (default: true) */
  preventDefault?: boolean;
  /** Stop propagation of touch events (default: false) */
  stopPropagation?: boolean;
}

export interface SwipeGestureState {
  /** Current swipe direction if active */
  direction: 'left' | 'right' | 'up' | 'down' | null;
  /** Is currently swiping */
  isSwiping: boolean;
  /** Distance swiped in pixels */
  distance: { x: number; y: number };
  /** Velocity of swipe in pixels/ms */
  velocity: { x: number; y: number };
  /** Starting position of swipe */
  startPosition: { x: number; y: number } | null;
  /** Current position during swipe */
  currentPosition: { x: number; y: number } | null;
}

export interface SwipeGestureHandlers {
  onSwipeStart?: (state: SwipeGestureState) => void;
  onSwipeMove?: (state: SwipeGestureState) => void;
  onSwipeEnd?: (state: SwipeGestureState) => void;
  onSwipeLeft?: (state: SwipeGestureState) => void;
  onSwipeRight?: (state: SwipeGestureState) => void;
  onSwipeUp?: (state: SwipeGestureState) => void;
  onSwipeDown?: (state: SwipeGestureState) => void;
}

export interface SwipeGestureResult {
  onTouchStart: React.TouchEventHandler;
  onTouchMove: React.TouchEventHandler;
  onTouchEnd: React.TouchEventHandler;
  onMouseDown: React.MouseEventHandler;
  onMouseMove?: React.MouseEventHandler;
  onMouseUp: React.MouseEventHandler;
  onMouseLeave?: React.MouseEventHandler;
  state: SwipeGestureState;
}

/**
 * Hook for detecting swipe gestures on touch devices
 * Returns event handlers to attach to the target element
 */
export function useSwipeGesture(
  handlers: SwipeGestureHandlers = {},
  options: SwipeGestureOptions = {}
): SwipeGestureResult {
  const {
    threshold = 50,
    maxTime = 500,
    horizontal = true,
    vertical = false,
    preventDefault = true,
    stopPropagation = false,
  } = options;

  const [state, setState] = React.useState<SwipeGestureState>({
    direction: null,
    isSwiping: false,
    distance: { x: 0, y: 0 },
    velocity: { x: 0, y: 0 },
    startPosition: null,
    currentPosition: null,
  });

  const startTimeRef = React.useRef<number>(0);
  const rafRef = React.useRef<number | undefined>(undefined);

  const updateState = React.useCallback((updates: Partial<SwipeGestureState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const getPosition = React.useCallback(
    (event: React.TouchEvent | React.MouseEvent | TouchEvent | MouseEvent) => {
      if ('touches' in event) {
        const touch = event.touches[0] || event.changedTouches[0];
        return { x: touch.clientX, y: touch.clientY };
      }
      return { x: event.clientX, y: event.clientY };
    },
    []
  );

  const calculateDirection = React.useCallback(
    (deltaX: number, deltaY: number): SwipeGestureState['direction'] => {
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (horizontal && absX > threshold && absX > absY) {
        return deltaX > 0 ? 'right' : 'left';
      }
      if (vertical && absY > threshold && absY > absX) {
        return deltaY > 0 ? 'down' : 'up';
      }
      return null;
    },
    [horizontal, vertical, threshold]
  );

  const handleStart = React.useCallback(
    (event: React.TouchEvent | React.MouseEvent | TouchEvent | MouseEvent) => {
      if (preventDefault) event.preventDefault();
      if (stopPropagation) event.stopPropagation();

      const position = getPosition(event);
      startTimeRef.current = Date.now();

      const newState: SwipeGestureState = {
        direction: null,
        isSwiping: true,
        distance: { x: 0, y: 0 },
        velocity: { x: 0, y: 0 },
        startPosition: position,
        currentPosition: position,
      };

      setState(newState);
      handlers.onSwipeStart?.(newState);
    },
    [getPosition, handlers, preventDefault, stopPropagation]
  );

  const handleMove = React.useCallback(
    (event: React.TouchEvent | React.MouseEvent | TouchEvent | MouseEvent) => {
      if (!state.isSwiping || !state.startPosition) return;

      if (preventDefault) event.preventDefault();
      if (stopPropagation) event.stopPropagation();

      // Cancel any pending RAF update
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      // Use RAF for smooth updates
      rafRef.current = requestAnimationFrame(() => {
        const currentPosition = getPosition(event);
        const deltaX = currentPosition.x - state.startPosition!.x;
        const deltaY = currentPosition.y - state.startPosition!.y;
        const deltaTime = Date.now() - startTimeRef.current;

        const velocityX = deltaTime > 0 ? deltaX / deltaTime : 0;
        const velocityY = deltaTime > 0 ? deltaY / deltaTime : 0;

        const direction = calculateDirection(deltaX, deltaY);

        const newState: SwipeGestureState = {
          direction,
          isSwiping: true,
          distance: { x: deltaX, y: deltaY },
          velocity: { x: velocityX, y: velocityY },
          startPosition: state.startPosition,
          currentPosition,
        };

        setState(newState);
        handlers.onSwipeMove?.(newState);
      });
    },
    [
      state.isSwiping,
      state.startPosition,
      getPosition,
      calculateDirection,
      handlers,
      preventDefault,
      stopPropagation,
    ]
  );

  const handleEnd = React.useCallback(
    (event: React.TouchEvent | React.MouseEvent | TouchEvent | MouseEvent) => {
      if (!state.isSwiping || !state.startPosition) return;

      if (preventDefault) event.preventDefault();
      if (stopPropagation) event.stopPropagation();

      const deltaTime = Date.now() - startTimeRef.current;
      const position = getPosition(event);
      const deltaX = position.x - state.startPosition.x;
      const deltaY = position.y - state.startPosition.y;

      // Check if it was a valid swipe (within time limit)
      if (deltaTime <= maxTime) {
        const direction = calculateDirection(deltaX, deltaY);

        const finalState: SwipeGestureState = {
          ...state,
          direction,
          isSwiping: false,
          currentPosition: position,
        };

        // Call direction-specific handlers
        if (direction === 'left') handlers.onSwipeLeft?.(finalState);
        if (direction === 'right') handlers.onSwipeRight?.(finalState);
        if (direction === 'up') handlers.onSwipeUp?.(finalState);
        if (direction === 'down') handlers.onSwipeDown?.(finalState);

        handlers.onSwipeEnd?.(finalState);
      }

      // Reset state
      updateState({
        direction: null,
        isSwiping: false,
        distance: { x: 0, y: 0 },
        velocity: { x: 0, y: 0 },
        startPosition: null,
        currentPosition: null,
      });

      // Clean up RAF
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    },
    [
      state,
      getPosition,
      calculateDirection,
      handlers,
      maxTime,
      updateState,
      preventDefault,
      stopPropagation,
    ]
  );

  // Clean up on unmount
  React.useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  // Return event handlers to attach to element
  return {
    onTouchStart: handleStart as React.TouchEventHandler,
    onTouchMove: handleMove as React.TouchEventHandler,
    onTouchEnd: handleEnd as React.TouchEventHandler,
    onMouseDown: handleStart as React.MouseEventHandler,
    onMouseMove: state.isSwiping ? (handleMove as React.MouseEventHandler) : undefined,
    onMouseUp: handleEnd as React.MouseEventHandler,
    onMouseLeave: state.isSwiping ? (handleEnd as React.MouseEventHandler) : undefined,
    state,
  };
}
